import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, QrCode, Box, Clock, Camera, Upload, RotateCw, Edit, Crop, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import BackButton from '@/components/BackButton';

const StandortDetailPage = () => {
  const { id } = useParams();
  const { user, API } = useContext(AuthContext);
  const [standort, setStandort] = useState(null);
  const [automaten, setAutomaten] = useState([]);
  const [besuche, setBesuche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [tempImage, setTempImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [standortRes, automatenRes, besucheRes] = await Promise.all([
        axios.get(`${API}/standorte/${id}`),
        axios.get(`${API}/automaten`),
        axios.get(`${API}/besuche/standort/${id}`),
      ]);
      setStandort(standortRes.data);
      const filtered = automatenRes.data.filter(a => a.current_location_id === id);
      setAutomaten(filtered);
      setBesuche(besucheRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleResetLocationDefect = async () => {
    try {
      // Setze sowohl location_is_defect auf false als auch Status auf "aktiv"
      await axios.put(`${API}/standorte/${id}`, {
        location_is_defect: false,
        status: 'aktiv'
      });
      toast.success('Standort-Defekt behoben!');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Zurücksetzen';
      toast.error(errorMsg);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild ist zu groß (max. 5MB)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      setTempImage(evt.target.result);
      setRotation(0);
      setCrop(undefined);
      setEditDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const getCroppedImg = (image, crop, rotation = 0) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = image;

    return new Promise((resolve) => {
      img.onload = () => {
        // Calculate dimensions based on rotation
        const isRotated = rotation === 90 || rotation === 270;
        const sourceWidth = isRotated ? img.height : img.width;
        const sourceHeight = isRotated ? img.width : img.height;

        // If crop exists, use crop dimensions, otherwise use full image
        let outputWidth, outputHeight, sx, sy, sw, sh;
        
        if (crop && crop.width && crop.height) {
          const scaleX = img.width / (imgRef.current?.width || img.width);
          const scaleY = img.height / (imgRef.current?.height || img.height);
          
          sx = crop.x * scaleX;
          sy = crop.y * scaleY;
          sw = crop.width * scaleX;
          sh = crop.height * scaleY;
          
          if (isRotated) {
            outputWidth = sh;
            outputHeight = sw;
          } else {
            outputWidth = sw;
            outputHeight = sh;
          }
        } else {
          sx = 0;
          sy = 0;
          sw = img.width;
          sh = img.height;
          outputWidth = isRotated ? sh : sw;
          outputHeight = isRotated ? sw : sh;
        }

        // Set canvas size
        canvas.width = outputWidth;
        canvas.height = outputHeight;

        // Apply rotation
        ctx.translate(outputWidth / 2, outputHeight / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        
        if (isRotated) {
          ctx.drawImage(img, sx, sy, sw, sh, -outputHeight / 2, -outputWidth / 2, sh, sw);
        } else {
          ctx.drawImage(img, sx, sy, sw, sh, -outputWidth / 2, -outputHeight / 2, sw, sh);
        }

        // Convert to base64 with quality
        const croppedImage = canvas.toDataURL('image/jpeg', 0.85);
        resolve(croppedImage);
      };
    });
  };

  const handleSaveImage = async () => {
    if (!tempImage) return;

    setUploading(true);
    try {
      const processedImage = await getCroppedImg(tempImage, completedCrop, rotation);

      await axios.put(`${API}/standorte/${id}`, {
        ...standort,
        primary_photo: processedImage
      });

      toast.success('Bild erfolgreich gespeichert!');
      setEditDialogOpen(false);
      setTempImage(null);
      setRotation(0);
      setCrop(undefined);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern des Bildes');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${API}/standorte/${id}`);
      const data = response.data;
      
      // Erfolg-Toast mit Info über betroffene Automaten
      if (data.affected_automaten_count > 0) {
        toast.success(
          `Standort "${data.deleted_standort_name}" gelöscht. ${data.affected_automaten_count} Automat(en) auf AUSZÄHLEN gesetzt.`
        );
      } else {
        toast.success(`Standort "${data.deleted_standort_name}" erfolgreich gelöscht`);
      }
      
      // Zur Standort-Übersicht navigieren
      window.location.href = '/standorte';
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  if (!standort) {
    return <div className="text-center py-12">Standort nicht gefunden</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="standort-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <BackButton />
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              {standort.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-slate-600">{standort.code}</p>
              {standort.key_number && (
                <span className="text-lg font-semibold text-blue-600 flex items-center gap-1">
                  🔑 {standort.key_number}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/standorte/scan/${standort.qr_value}`}>
            <Button className="flex items-center space-x-2" data-testid="scan-button">
              <QrCode size={20} />
              <span>Gehäuse scannen</span>
            </Button>
          </Link>
          {user?.role === 'admin' && (
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(true)}
              className="flex items-center space-x-2 border-red-300 text-red-700 hover:bg-red-50"
              data-testid="delete-standort-button"
            >
              <Trash2 size={20} />
              <span>Standort löschen</span>
            </Button>
          )}
        </div>
      </div>

      {/* Standort Info */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Image Section */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
              <Camera className="text-blue-600" size={24} />
              <span>Foto</span>
            </h2>
            {standort.primary_photo ? (
              <div className="relative">
                <img 
                  src={standort.primary_photo} 
                  alt={standort.name}
                  className="w-full h-48 object-cover rounded-lg border-2 border-slate-200"
                />
                {user?.role === 'admin' && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      disabled={uploading}
                      data-testid="change-photo-button"
                    >
                      <Edit size={16} className="mr-2" />
                      Bearbeiten
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-slate-100 h-48 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center">
                <Camera className="text-slate-400 mb-2" size={48} />
                <p className="text-slate-500 text-sm mb-3">Kein Foto vorhanden</p>
                {user?.role === 'admin' && (
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    size="sm"
                    disabled={uploading}
                    data-testid="upload-photo-button"
                  >
                    <Upload size={16} className="mr-2" />
                    Foto hochladen
                  </Button>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
              <MapPin className="text-blue-600" size={24} />
              <span>Standortinformationen</span>
            </h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Adresse:</span> {standort.street}, {standort.zip} {standort.city}
                {standort.district && <span className="text-slate-500"> ({standort.district})</span>}
              </div>
              <div><span className="font-medium">Beschreibung:</span> {standort.location_description}</div>
              <div><span className="font-medium">Gehäusetyp:</span> {standort.housing_type}</div>
              <div><span className="font-medium">Befüllzyklus:</span> {standort.refill_cycle_days} Tage</div>
              <div>
                <span className="font-medium">Status:</span>{' '}
                {/* Einheitliche Defektlogik: location_is_defect ODER Status "Schaden"/"defekt gemeldet" = defekt */}
                {(standort.location_is_defect || standort.status === 'Schaden' || standort.status === 'defekt gemeldet') ? (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    ⚠️ defekt
                  </span>
                ) : (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    standort.status === 'aktiv' ? 'bg-green-100 text-green-700' :
                    standort.status === 'LEER' ? 'bg-red-100 text-red-700' :
                    standort.status === 'veraltet' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {standort.status}
                  </span>
                )}
              </div>
              {/* Aktiver Defekt-Alarm: Nur wenn location_is_defect=true ODER status="Schaden"/"defekt gemeldet" */}
              {(standort.location_is_defect || standort.status === 'Schaden' || standort.status === 'defekt gemeldet') && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mt-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className="font-medium text-red-700">⚠️ Standort ist defekt</span>
                      {standort.damage_note && (
                        <p className="text-sm text-red-600 mt-1">{standort.damage_note}</p>
                      )}
                    </div>
                    <Button
                      onClick={handleResetLocationDefect}
                      size="sm"
                      variant="outline"
                      className="ml-2 border-red-300 text-red-700 hover:bg-red-100"
                    >
                      Defekt beheben
                    </Button>
                  </div>
                </div>
              )}
              {/* Vergangener Schaden: Neutral als Historie anzeigen wenn damage_note vorhanden aber kein aktiver Defekt */}
              {standort.damage_note && !standort.location_is_defect && standort.status !== 'Schaden' && standort.status !== 'defekt gemeldet' && (
                <div className="bg-slate-50 border border-slate-200 rounded p-2 mt-2">
                  <span className="font-medium text-slate-600">📋 Historie (Schaden behoben):</span>
                  <p className="text-slate-500 mt-1">{standort.damage_note}</p>
                </div>
              )}
              {standort.task && (
                <div className="bg-purple-50 border border-purple-200 rounded p-2 mt-2">
                  <span className="font-medium text-purple-700">📋 Aufgabe:</span>
                  <p className="text-purple-600 mt-1">{standort.task}</p>
                </div>
              )}
              {standort.notes && (
                <div className="bg-slate-50 border border-slate-200 rounded p-2 mt-2">
                  <span className="font-medium text-slate-700">📝 Bemerkung:</span>
                  <p className="text-slate-600 mt-1">{standort.notes}</p>
                </div>
              )}
            </div>
          </div>
          <div>
            <h2 className="text-xl font-bold mb-4">Kontakt</h2>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Name:</span> {standort.contact_name}</div>
              <div><span className="font-medium">Telefon:</span> {standort.contact_phone}</div>
              {standort.contact_email && <div><span className="font-medium">E-Mail:</span> {standort.contact_email}</div>}
            </div>
          </div>
        </div>
      </Card>

      {/* Automaten im Gehäuse */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center space-x-2">
            <Box className="text-blue-600" size={24} />
            <span>Automaten im Gehäuse ({automaten.length}/{standort.slot_count})</span>
          </h2>
          {user?.role === 'admin' && (
            <Link to={`/standorte/scan/${standort.qr_value}`}>
              <Button className="flex items-center space-x-2 bg-green-600 hover:bg-green-700">
                <Box size={20} />
                <span>Bestücken</span>
              </Button>
            </Link>
          )}
        </div>
        {automaten.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-500 mb-4">Keine Automaten zugeordnet</p>
            {user?.role === 'admin' && (
              <Link to={`/standorte/scan/${standort.qr_value}`}>
                <Button variant="outline">
                  <Box size={16} className="mr-2" />
                  Jetzt bestücken
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {automaten.map((automat) => (
              <Link key={automat.id} to={`/automaten/${automat.id}`}>
                <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`automat-${automat.code}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold">{automat.code}</p>
                      <p className="text-sm text-slate-600">Slot {automat.current_slot_number || '?'} | {automat.machine_type} | {automat.coin_type} €</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      automat.is_defect ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {automat.is_defect ? 'Defekt' : 'OK'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {/* Besuchshistorie */}
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
          <Clock className="text-blue-600" size={24} />
          <span>Besuchshistorie</span>
        </h2>
        {besuche.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Noch keine Besuche</p>
        ) : (
          <div className="space-y-3">
            {besuche.slice(0, 10).map((besuch) => (
              <div key={besuch.id} className="p-4 bg-slate-50 rounded-lg" data-testid={`besuch-${besuch.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">
                      {format(new Date(besuch.started_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </p>
                  </div>
                  {besuch.finished_at ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Abgeschlossen</span>
                  ) : (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Laufend</span>
                  )}
                </div>
                {/* Detaillierte Slot-Anzeige */}
                <div className="space-y-1">
                  {besuch.slots && besuch.slots.length > 0 ? (
                    besuch.slots.map((slot, idx) => (
                      <div key={idx} className="text-sm">
                        {besuch.slots.length > 1 && (
                          <span className="text-slate-500 mr-1">Schacht {slot.slot_number || idx + 1}:</span>
                        )}
                        <span className={`${slot.display_type === 'leerung' ? 'text-amber-600' : slot.display_type === 'wechsel' ? 'text-blue-600' : 'text-slate-600'}`}>
                          {slot.display_text || `${slot.action || 'Aktion'}`}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">Keine Slot-Aktionen</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Bildbearbeitungs-Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Bild bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center items-center bg-slate-100 rounded-lg p-4 min-h-[400px]">
              {tempImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={undefined}
                >
                  <img
                    ref={imgRef}
                    src={tempImage}
                    alt="Zu bearbeiten"
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      maxHeight: '500px',
                      maxWidth: '100%'
                    }}
                  />
                </ReactCrop>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleRotate}
                variant="outline"
                className="flex-1"
                data-testid="rotate-button"
              >
                <RotateCw size={16} className="mr-2" />
                Drehen (90°)
              </Button>
              <Button
                onClick={handleSaveImage}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600"
                disabled={uploading}
                data-testid="save-image-button"
              >
                {uploading ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
            <p className="text-sm text-slate-500 text-center">
              Ziehen Sie einen Bereich, um das Bild zuzuschneiden (optional)
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 size={20} />
              Standort wirklich löschen?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Möchten Sie den Standort <strong>&ldquo;{standort.name}&rdquo;</strong> (Code: {standort.code}) wirklich löschen?
            </p>
            
            {/* Warnung bei vorhandenen Automaten */}
            {automaten.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 font-medium mb-1">
                  ⚠️ {automaten.length} Automat(en) an diesem Standort:
                </p>
                <ul className="text-sm text-amber-700 list-disc list-inside mb-2">
                  {automaten.slice(0, 5).map(a => (
                    <li key={a.id}>{a.code}</li>
                  ))}
                  {automaten.length > 5 && (
                    <li>... und {automaten.length - 5} weitere</li>
                  )}
                </ul>
                <p className="text-sm text-amber-800">
                  Diese Automaten werden automatisch auf <strong>&bdquo;AUSZÄHLEN&ldquo;</strong> gesetzt und vom Standort entkoppelt.
                </p>
              </div>
            )}
            
            {/* Info über Datenerhaltung */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                ✓ Kassenbuch und Umsätze bleiben vollständig erhalten.
              </p>
            </div>
            
            <p className="text-sm text-slate-600">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-button"
              >
                <Trash2 size={16} className="mr-2" />
                Löschen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandortDetailPage;
